import { useState, useRef, useEffect, useCallback } from 'react';
import * as api from '../services/api-client';

/**
 * Represents a user from the EntraID directory.
 */
export interface DirectoryUser {
  readonly id: string;
  readonly displayName: string;
  readonly mail: string | null;
  readonly accountEnabled: boolean;
}

interface SearchUsersResponse {
  readonly users: DirectoryUser[];
}

/**
 * Return type for the useUserSearch hook.
 */
export interface UseUserSearchReturn {
  readonly results: DirectoryUser[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly search: (query: string) => void;
  readonly clear: () => void;
}

/**
 * Hook providing debounced user search against the EntraID directory.
 *
 * - Debounces search calls by 300ms
 * - Only triggers when query is >= 2 characters
 * - Cancels in-flight requests when a new search triggers
 * - Cleans up on unmount
 *
 * Validates: Requirements 3.1, 3.4, 3.5
 */
export function useUserSearch(): UseUserSearchReturn {
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Ref for the debounce timeout
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Ref for tracking the current request version to discard stale responses
  const requestVersionRef = useRef(0);

  const clearTimeout_ = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const clear = useCallback(() => {
    clearTimeout_();
    // Increment version to cancel any in-flight request
    requestVersionRef.current += 1;
    setResults([]);
    setError(null);
    setIsLoading(false);
  }, [clearTimeout_]);

  const search = useCallback((query: string) => {
    // Clear any pending debounce
    clearTimeout_();

    // If query is less than 2 characters, clear results and return
    if (query.length < 2) {
      // Cancel in-flight by bumping version
      requestVersionRef.current += 1;
      setResults([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    // Set debounced search
    timeoutRef.current = setTimeout(async () => {
      // Capture current version before the request
      requestVersionRef.current += 1;
      const version = requestVersionRef.current;

      try {
        const response = await api.get<SearchUsersResponse>(
          '/admin/users/search',
          { q: query },
        );

        // Only apply results if this is still the latest request
        if (version === requestVersionRef.current) {
          setResults(response.users);
          setError(null);
          setIsLoading(false);
        }
      } catch (err) {
        // Only apply error if this is still the latest request
        if (version === requestVersionRef.current) {
          if (err instanceof api.ApiErrorResponse && err.status === 502) {
            setError('Directory service unavailable. Try again later.');
          } else if (err instanceof api.NetworkError) {
            setError('Network error. Please check your connection.');
          } else {
            setError('An error occurred while searching.');
          }
          setResults([]);
          setIsLoading(false);
        }
      }
    }, 300);
  }, [clearTimeout_]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout_();
      // Increment version to discard any pending responses
      requestVersionRef.current += 1;
    };
  }, [clearTimeout_]);

  return { results, isLoading, error, search, clear };
}
